import React, { useState } from 'react';
import { ReunionCategory } from '@teetsh/app/src/types/reunions';
import usePlanner from '@teetsh/app/src/queries/planners/usePlanner';
import SpinnerComponent from '@teetsh/app/src/components/Spinner/ScreenSpinner';
import { Body } from './Body';
import { Sidebar } from './Sidebar';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import 'twin.macro';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import useUpdateUser from '@teetsh/app/src/queries/user/useUpdateUser';
import { useUserAccount } from '@teetsh/app/src/queries/user/account/useUserAccount';
import {
  Planner,
  PlannerCalendarPDFSettings,
} from '@teetsh/app/src/types/planner';
import { Form } from 'react-final-form';
import AutoSubmitForm from '@teetsh/app/src/components/AutoSave';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@teetsh/app/src/components/Resizable';
import { useUpdatePlannerDebounce } from '@teetsh/app/src/queries/planners/useUpdatePlannerDebounce';
import parse from '@teetsh/app/src/utils/time/parse';
import { isoDatePattern } from '@teetsh/app/src/utils/time/datePatterns';

interface Props {
  planner: Planner;
}

function PreviewPlannerPDF(props: Props) {
  const user = useUser();
  const updateUser = useUpdateUser();
  const { data: userAccount } = useUserAccount();

  const { categories } = useOutletContext<{
    categories: ReunionCategory[];
  }>();

  const [params] = useSearchParams();
  const currentDate = params.get('date')
    ? parse(params.get('date')!, isoDatePattern)
    : new Date();

  const [planner, updatePlanner] = useUpdatePlannerDebounce(props.planner);

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  return (
    <Form<PlannerCalendarPDFSettings>
      initialValues={user.fonts.plannerCalendar}
      onSubmit={async values => {
        await updateUser({
          ...user,
          fonts: {
            ...user.fonts,
            plannerCalendar: values,
          },
        });
        return {};
      }}
    >
      {({ handleSubmit }) => (
        <form tw="min-h-0 h-full" onSubmit={handleSubmit}>
          <AutoSubmitForm />
          <div tw="w-full h-full flex flex-col overflow-hidden">
            <ResizablePanelGroup
              direction="horizontal"
              autoSaveId="planner-preview-pdf-sidebar"
            >
              <Sidebar
                date={currentDate}
                planner={planner}
                onUpdatePlanner={updatePlanner}
                lng={user.locale}
                limitedBgSet={userAccount?.isOnOldPricing || false}
                categories={categories}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
              />
              <ResizableHandle />
              <ResizablePanel order={1}>
                <div tw="h-full overflow-auto">
                  <Body
                    planner={planner}
                    date={currentDate}
                    lng={user.locale}
                    selectedCategories={selectedCategories}
                  />
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>
        </form>
      )}
    </Form>
  );
}

export function PreviewPlannerPDFData() {
  const { data: planner, isLoading } = usePlanner();
  if (!planner || isLoading) {
    return <SpinnerComponent />;
  }
  return <PreviewPlannerPDF planner={planner} />;
}
